import { useEffect, Component, type ReactNode, type ErrorInfo } from "react";
import { createPortal } from "react-dom";
import { QueryProvider } from "@/providers/QueryProvider";
import { CookieConsentProvider } from "@/providers/CookieConsentProvider";
import { WootricProvider } from "@/providers/WootricProvider";
import { useUserProfile } from "@/hooks/useUserProfile";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { BookingValidationDialog } from "@/components/booking/BookingValidationDialog";
import { useAuth } from "@/providers/AuthProvider";
import { BryntumThemeProvider } from "@/contexts/BryntumThemeContext";
import { setApiUrl } from "@/lib/api-config";
import { configureAuth } from "@/lib/auth-fetch";
import App from "@/App";
import type { WootricDomain } from "@/types/wootric";
import { FeedbackConfigProvider } from "@/providers/FeedbackConfigProvider";

interface AppContainerProps {
  settings?: {
    apiUrl?: string;
    clientId?: string;
    defaultUrl?: string;
    identityUrl?: string;
    redirectUri?: string;
    scope?: string;
    workspaceUrl?: string;
    wootricDomain?: WootricDomain;
    sentimentAccountToken?: string;
    sentimentApiHost?: string;
    sentimentReDisplayAfterDays?: number;
    sentimentMfeVersion?: string;
    mfeRegistryBase?: string;
  };
  defaultDataMode?: 'demo' | 'live';
  liveOnly?: boolean;
}

// ── Error Boundary ─────────────────────────────────────────────────────────────
interface EBState { error: Error | null }
class AppErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, fontFamily: 'monospace', background: '#fff', color: '#c00', whiteSpace: 'pre-wrap', fontSize: 13 }}>
          <strong>App crashed — see console for details</strong>{'\n\n'}
          {this.state.error.toString()}{'\n\n'}
          {this.state.error.stack}
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Main Component ─────────────────────────────────────────────────────────────
function AppContent({
  defaultDataMode,
  liveOnly,
  wootricDomain,
}: {
  defaultDataMode?: 'demo' | 'live';
  liveOnly?: boolean;
  wootricDomain?: WootricDomain;
}) {
  const { user: wootricUser } = useUserProfile();

  return (
    <WootricProvider user={wootricUser} wootricDomainOverride={wootricDomain}>
      <App defaultDataMode={defaultDataMode} liveOnly={liveOnly} />
    </WootricProvider>
  );
}

export default function AppContainer({ settings, defaultDataMode, liveOnly }: AppContainerProps = {}): React.ReactElement {
  const { getAccessToken, instanceId, evoOrganisationId } = useAuth();

  // Set apiUrl synchronously before render to ensure it's available for API calls
  if (settings?.apiUrl) {
    console.log('[AppContainer] Setting API URL to:', settings.apiUrl);
    setApiUrl(settings.apiUrl);
  } else {
    console.log('[AppContainer] No apiUrl in settings, not calling setApiUrl');
  }

  useEffect(() => {
    configureAuth({ getAccessToken, instanceId, evoOrganisationId });
  }, [getAccessToken, instanceId, evoOrganisationId]);

  return (
    <AppErrorBoundary>
      <FeedbackConfigProvider
        accountToken={settings?.sentimentAccountToken}
        apiHost={settings?.sentimentApiHost}
        reDisplayAfterDays={settings?.sentimentReDisplayAfterDays}
        mfeRegistryBase={settings?.mfeRegistryBase}
        mfeVersion={settings?.sentimentMfeVersion}
      >
        <QueryProvider>
          <CookieConsentProvider>
            <TooltipProvider>
              <BryntumThemeProvider>
                {/*
                  Toasts are portalled to document.body so they share a stacking
                  context with the Radix overlay portals (sheets, dialogs), which are
                  also body children. Rendered inside #root — which is
                  `position: fixed` and therefore its own stacking context — the
                  toaster's z-index cannot compete with those overlays and toasts
                  paint behind any open sheet or dialog.
                */}
                {createPortal(
                  // Radix sets `pointer-events: none` on <body> while a modal sheet or
                  // dialog is open; the wrapper restores it for toasts so they stay
                  // hoverable and dismissable. It also keeps pointer events inside the
                  // toaster, so interacting with a toast is not treated as an outside
                  // interaction that dismisses the open sheet or dialog.
                  <div
                    className="pointer-events-auto"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <Toaster richColors duration={5000} closeButton />
                  </div>,
                  document.body,
                )}
                <AppContent
                  defaultDataMode={defaultDataMode}
                  liveOnly={liveOnly}
                  wootricDomain={settings?.wootricDomain}
                />
                <BookingValidationDialog />
              </BryntumThemeProvider>
            </TooltipProvider>
          </CookieConsentProvider>
        </QueryProvider>
      </FeedbackConfigProvider>
    </AppErrorBoundary>
  );
}
